import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterPaarl() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-paarl",
    name: "Painters Paarl",
    location: "Paarl, Western Cape",
    rating: 4.8,
    reviews: 167,
    specialties: ["Interior", "Exterior", "Wine Estate", "Heritage Properties"],
    image: "https://public.readdy.ai/ai/img_res/c9ae7baaacab177512b221771478352e.jpg",
    verified: true,
    yearsExperience: 16,
    website: "https://painterspaarl.co.za",
    email: "info@painterspaarl.co.za",
    description: "Professional painting services in Paarl and surrounding wine regions. Specializing in heritage properties, wine estates, and modern homes with attention to architectural character.",
    services: [
      "Wine Estate Painting",
      "Heritage Property Restoration", 
      "Cape Dutch Architecture",
      "Cellar and Tasting Room Painting",
      "Vineyard Building Maintenance",
      "Modern Home Painting",
      "Commercial Wine Facility Painting",
      "Decorative and Specialty Finishes"
    ],
    serviceAreas: ["Paarl", "Wellington", "Franschhoek", "Stellenbosch", "Winelands", "Western Cape"],
    gallery: [
      "https://readdy.ai/api/search-image?query=Beautiful%20wine%20estate%20painted%20by%20professional%20painters%20in%20Paarl%2C%20Cape%20Dutch%20architecture%20with%20fresh%20painted%20walls%2C%20quality%20paint%20finish%2C%20vineyard%20views%2C%20bright%20natural%20lighting%2C%20clean%20professional%20painting%20work&width=400&height=300&seq=paarl-gallery-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Heritage%20property%20painting%20project%20in%20Paarl%20Western%20Cape%2C%20Cape%20Dutch%20home%20with%20fresh%20paint%2C%20professional%20painters%20working%2C%20quality%20exterior%20finish%2C%20mountain%20setting%20with%20vineyards%2C%20well-maintained%20property&width=400&height=300&seq=paarl-gallery-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Wine%20cellar%20painting%20project%20in%20Paarl%2C%20professional%20painters%20working%20on%20winery%20facility%2C%20quality%20commercial%20paint%20finish%2C%20modern%20wine%20production%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=paarl-gallery-3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Tasting%20room%20interior%20painting%20in%20Paarl%20wine%20estate%2C%20fresh%20painted%20walls%2C%20elegant%20wine%20tasting%20space%2C%20quality%20paint%20finish%2C%20bright%20welcoming%20atmosphere%2C%20professional%20painting%20work&width=400&height=300&seq=paarl-gallery-4&orientation=landscape"
    ],
    testimonials: [
      {
        name: "Johann van der Merwe",
        rating: 5,
        comment: "Outstanding work on our wine estate in Paarl. The painters understood the heritage requirements and delivered a beautiful restoration that honors our Cape Dutch architecture while meeting modern standards."
      },
      {
        name: "Marie Dubois", 
        rating: 5,
        comment: "Professional service from start to finish. They completed our winery facility painting on schedule and the quality exceeded our expectations. Highly recommended for wine estate projects."
      },
      {
        name: "David Botha",
        rating: 4,
        comment: "Excellent attention to detail and very knowledgeable about heritage properties. Our Paarl home looks magnificent and the color consultation was particularly valuable."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Paarl | Professional House Painting Services Winelands | Residential & Commercial";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Painters Paarl provides professional painting services in Paarl, Cape Winelands. Specializing in residential and commercial painting, wine estates, and heritage properties. Quality painting contractors in the Winelands.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Paarl, house painters Paarl, painting contractors Winelands, residential painting, wine estate painting');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-blue-600 to-blue-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20wine%20estate%20property%20in%20Paarl%20South%20Africa%2C%20beautiful%20vineyard%20setting%20with%20mountain%20backdrop%2C%20painters%20in%20clean%20uniforms%20with%20quality%20equipment%2C%20Cape%20Dutch%20architecture%20and%20modern%20buildings%2C%20bright%20natural%20lighting%2C%20high%20quality%20heritage%20painting%20project&width=1200&height=600&seq=paarl-painters-hero&orientation=landscape)` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white">
            <div className="flex items-center mb-4">
              <h1 className="text-4xl md:text-5xl font-bold mr-4">{painter.name}</h1>
              {painter.verified && (
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-shield-check-line mr-1"></i>
                  Verified
                </div>
              )}
            </div>
            
            <p className="text-xl mb-4 flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              {painter.location}
            </p>
            
            <div className="flex items-center space-x-6 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`ri-star-fill ${i < Math.floor(painter.rating) ? 'text-yellow-400' : 'text-gray-400'}`}></i>
                ))}
                <span className="ml-2">{painter.rating} ({painter.reviews} reviews)</span>
              </div>
              <span>{painter.yearsExperience} years experience</span>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => navigate('/quote')}
                className="bg-lime-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'services', label: 'Services' },
              { id: 'gallery', label: 'Gallery' },
              { id: 'reviews', label: 'Reviews' },
              { id: 'contact', label: 'Contact' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">About Painters Paarl - Your Wine Estate and Heritage Property Specialists</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  {painter.description} With over 16 years of specialized experience in the Winelands region, we have established ourselves as the premier choice for wine estates, heritage properties, and discerning homeowners who demand excellence in both craftsmanship and understanding of architectural heritage.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Wine Estate Painting Specialists</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Paarl's wine estates represent some of South Africa's most prestigious and historically significant properties, requiring specialized painting services that understand both the functional demands of wine production and the aesthetic requirements of hospitality venues. Our team has extensive experience working with wine estates throughout the region, from intimate boutique wineries to large commercial operations, ensuring that every project enhances the property's character while meeting the practical needs of wine production and tourism.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Cape Dutch Architecture Expertise</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The Paarl region is renowned for its magnificent Cape Dutch architecture, with many properties dating back centuries and requiring careful restoration that honors their historical significance. Our team has specialized training in heritage conservation techniques, working closely with heritage consultants and conservation authorities to ensure all restoration work meets strict conservation standards while incorporating modern protective technologies that extend the life of these precious architectural treasures.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Winelands Climate Considerations</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The unique microclimate of the Paarl Winelands, with its hot, dry summers and wet winters, creates specific challenges for paint systems that require specialized knowledge and materials. Our extensive experience in the region has taught us how to select and apply paint systems that withstand the extreme temperature variations, intense UV radiation, and seasonal moisture changes that characterize the Winelands climate, ensuring long-lasting protection and beauty for your property.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Cellar and Production Facility Expertise</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Wine production facilities require specialized coating systems that meet strict hygiene standards while withstanding the unique environmental conditions of cellars and production areas. Our team understands the specific requirements for food-grade coatings, moisture resistance, and temperature stability needed in wine production environments. We use only approved coating systems that maintain wine quality standards while providing durable, easy-to-clean surfaces that support efficient operations.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Hospitality and Tasting Room Excellence</h3>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Many wine estates in Paarl operate sophisticated hospitality venues, including tasting rooms, restaurants, and event facilities that require painting solutions balancing aesthetic appeal with practical durability. Our team specializes in creating beautiful, welcoming environments that enhance the wine tasting experience while withstanding the heavy use and frequent cleaning required in hospitality settings. We work closely with interior designers and hospitality consultants to achieve the perfect balance of beauty and functionality.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {painter.specialties.map((specialty, index) => (
                        <span key={index} className="bg-lime-100 text-lime-800 px-3 py-1 rounded-full text-sm font-medium">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                    <div className="space-y-2">
                      {painter.serviceAreas.slice(0, 4).map((area, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <i className="ri-map-pin-line mr-2 text-lime-500"></i>
                          {area}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                  <div className="mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20wine%20estate%20building%20in%20Paarl%20South%20Africa%2C%20winelands%20property%20with%20mountain%20views%2C%20painters%20in%20uniform%20with%20quality%20equipment%2C%20Cape%20Dutch%20architecture%2C%20organized%20work%20site%20with%20vineyard%20background&width=400&height=300&seq=paarl-sidebar-image&orientation=landscape"
                      alt="Professional painters Paarl wine estate painting services South Africa winelands Cape Dutch architecture"
                      className="w-full h-48 object-cover object-top rounded-lg"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-mail-line mr-3 text-lime-500"></i>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line mr-3 text-lime-500"></i>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        Visit Website
                      </a>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => navigate('/quote')}
                    className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Get Free Quote
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Comprehensive Wine Estate and Heritage Painting Services</h2>
              
              <div className="mb-12">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Specialized Winelands Painting Solutions</h3>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Our comprehensive painting services are specifically designed for the unique requirements of wine estates, heritage properties, and modern homes throughout the Paarl Winelands. From historic Cape Dutch homesteads to state-of-the-art wine production facilities, we provide specialized solutions that enhance both aesthetic appeal and functional performance.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {painter.services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center mb-4">
                      <i className="ri-brush-line text-2xl text-lime-500 mr-3"></i>
                      <h3 className="text-lg font-semibold text-gray-900">{service}</h3>
                    </div>
                    <p className="text-gray-600">Professional {service.toLowerCase()} services with heritage-appropriate materials and expert Winelands application techniques.</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Heritage Property Restoration</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our heritage property restoration services combine traditional techniques with modern protective technologies to preserve and enhance Paarl's magnificent historical buildings. We work closely with heritage consultants to ensure all restoration work meets conservation standards while providing long-lasting protection against the Winelands climate.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our team has specialized training in traditional lime-based paints, period-appropriate color schemes, and conservation techniques that honor the original craftsmanship while incorporating modern protective elements that extend the life of these precious architectural treasures.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Wine Production Facility Coatings</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Wine production facilities require specialized coating systems that meet strict hygiene and safety standards while withstanding the unique environmental conditions of cellars and production areas. Our food-grade coating systems provide superior protection while maintaining the optimal conditions for wine production and storage.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    We understand the critical importance of maintaining proper environmental conditions in wine production areas, using only approved coating systems that support temperature and humidity control while providing easy-to-clean, durable surfaces that meet the highest hygiene standards.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Recent Wine Estate and Heritage Projects in Paarl</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Explore our portfolio of recent painting projects throughout the Paarl Winelands. Each project demonstrates our expertise in heritage restoration, wine estate facilities, and modern property enhancement with attention to architectural character and functional requirements.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.gallery.map((image, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image} 
                      alt={`${painter.name} project ${index + 1}`}
                      className="w-full h-64 object-cover object-top"
                    />
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Paarl Winelands Project {index + 1}</h3>
                      <p className="text-gray-600 text-sm">Professional wine estate and heritage property painting with specialized materials and expert Winelands application techniques.</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews and Testimonials</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Read what our satisfied customers throughout the Paarl Winelands have to say about our specialized painting services. Our commitment to excellence in heritage restoration and wine estate projects is reflected in these genuine reviews from property owners and estate managers.
              </p>
              
              <div className="space-y-6">
                {painter.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                        ))}
                      </div>
                      <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{testimonial.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Painters Paarl</h2>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Ready to enhance your Paarl wine estate or heritage property with professional painting services? Contact us today for a free consultation and quote. We're here to help you achieve beautiful, lasting results that honor your property's character while meeting modern performance standards.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-center">
                    <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email</h3>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Website</h3>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        {painter.website}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Service Area</h3>
                      <p className="text-gray-700">{painter.location} and surrounding Winelands region</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Your Free Heritage Assessment</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Request a free, no-obligation assessment for your Paarl wine estate or heritage property painting project. Our experienced team will evaluate your property's specific requirements and provide detailed recommendations.
                </p>
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                >
                  Request Free Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
